import { useEffect, useRef } from 'react';
import { useEditor } from '../../state/editorStore';
import { CaptionCanvas } from '../CaptionCanvas';
import { BrollLayer } from './BrollLayer';
import { TransitionOverlay } from './TransitionOverlay';
import { SafeZoneOverlay } from './SafeZoneOverlay';
import { AudioEngine } from './AudioEngine';
import { SfxPlayer } from './SfxPlayer';
import { playbackClock } from '../../playback/clock';

const ASPECTS: Record<string, { w: number; h: number }> = {
  '9:16': { w: 720, h: 1280 },
  '16:9': { w: 1280, h: 720 },
  '1:1': { w: 1000, h: 1000 },
  '4:5': { w: 1024, h: 1280 },
};

/** The always-centered video with the live caption overlay. */
export function StudioStage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const bgRef = useRef<HTMLVideoElement>(null);
  const personRef = useRef<HTMLVideoElement>(null);
  const words = useEditor((s) => s.words);
  const segments = useEditor((s) => s.segments);
  const style = useEditor((s) => s.resolvedStyle());
  const placement = useEditor((s) => s.resolvedPlacement());
  const styleForSegment = useEditor((s) => s.styleForSegment);
  const placementForSegment = useEditor((s) => s.placementForSegment);
  const videoUrl = useEditor((s) => s.videoUrl);
  const hooks = useEditor((s) => s.hooks);
  const durationMs = useEditor((s) => s.durationMs);
  const cutout = useEditor((s) => s.cutout);
  const aspect = useEditor((s) => s.aspect);
  const sourceDims = useEditor((s) => s.sourceDims);
  // For a fixed ratio use the reference box; for 'source' use the clip's own
  // shape (falling back to portrait until the video's dimensions are known).
  const dims = aspect === 'source' ? (sourceDims ?? ASPECTS['9:16']!) : ASPECTS[aspect]!;

  // Show the live "behind the person" composite when EITHER a hook opts into it
  // OR the caption template renders behind the subject - and the cached person
  // cut-out is ready. The alpha WebM is layered between a behind-canvas (behind
  // hooks + behind caption words) and a front-canvas (front captions + hooks).
  const captionsBehind = useEditor((s) => s.template().renderMode === 'behind');
  const hasBehindHook = hooks.some((h) => h.behind);
  const showDepth = (hasBehindHook || captionsBehind) && cutout.status === 'ready' && !!cutout.url;

  useEffect(() => {
    playbackClock.attachMedia(videoRef.current ?? null);
    playbackClock.setDuration(durationMs);
    return () => playbackClock.attachMedia(null);
  }, [videoUrl, durationMs]);

  // Record the clip's native dimensions from the loaded video, so the 'source'
  // ratio previews faithfully (and as a fallback when the server didn't supply
  // them, e.g. legacy projects).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoUrl) return;
    const capture = () => {
      if (v.videoWidth && v.videoHeight) {
        useEditor.setState({ sourceDims: { w: v.videoWidth, h: v.videoHeight } });
      }
    };
    if (v.readyState >= 1) capture();
    else v.addEventListener('loadedmetadata', capture, { once: true });
    return () => v.removeEventListener('loadedmetadata', capture);
  }, [videoUrl]);

  // Keep the person cut-out video FRAME-synced to the main video (it has no
  // audio; the main video is the source of truth). Two independent <video>
  // elements drift, so instead of only hard-seeking on large drift we:
  //  - while playing: nudge the cut-out's playbackRate to smoothly converge on
  //    the main video's time (invisible with no audio), hard-seeking only on a
  //    big gap (a scrub/stall); a requestVideoFrameCallback loop corrects on
  //    every presented frame for tight locking.
  //  - while paused/scrubbing: seek the cut-out exactly to the main time.
  useEffect(() => {
    const pv = personRef.current;
    const mv = videoRef.current;
    if (!pv || !mv || !showDepth) return;
    let cancelled = false;

    const correct = () => {
      if (cancelled) return;
      const target = mv.currentTime;
      const diff = pv.currentTime - target; // >0: cut-out ahead, <0: behind
      if (playbackClock.isPlaying() && !mv.paused) {
        if (pv.paused) pv.play().catch(() => undefined);
        if (Math.abs(diff) > 0.2) {
          try { pv.currentTime = target; } catch { /* pre-metadata */ }
          pv.playbackRate = 1;
        } else {
          // Converge within ~1 frame: speed up when behind, slow when ahead.
          pv.playbackRate = Math.min(1.25, Math.max(0.8, 1 - diff * 2.5));
        }
      } else {
        if (!pv.paused) pv.pause();
        if (pv.playbackRate !== 1) pv.playbackRate = 1;
        if (Math.abs(diff) > 0.02) { try { pv.currentTime = target; } catch { /* noop */ } }
      }
    };

    // Fine-grained per-frame locking during playback (Chrome/Safari/Edge).
    type RVFC = HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number };
    const rvfc = pv as RVFC;
    if (typeof rvfc.requestVideoFrameCallback === 'function') {
      const loop = () => {
        if (cancelled) return;
        correct();
        rvfc.requestVideoFrameCallback!(loop);
      };
      rvfc.requestVideoFrameCallback(loop);
    }
    // Always-on driver for pause/scrub (and a fallback when rVFC is absent).
    const unsub = playbackClock.subscribe(correct);
    correct();
    return () => {
      cancelled = true;
      unsub();
      try { pv.playbackRate = 1; } catch { /* noop */ }
    };
  }, [showDepth, cutout.url]);

  // Backdrop fill: mirror the main video into a blurred, full-cover layer behind
  // the transforming content, so slide/zoom transitions reveal the same content
  // (not black). Uses captureStream so the mirror stays perfectly in sync.
  useEffect(() => {
    const v = videoRef.current;
    const bg = bgRef.current;
    if (!v || !bg || !videoUrl) return;
    const attach = () => {
      try {
        const stream = (v as HTMLVideoElement & { captureStream?: () => MediaStream }).captureStream?.();
        if (stream) {
          bg.srcObject = stream;
          bg.play().catch(() => undefined);
          return;
        }
      } catch {
        /* fall through */
      }
      bg.src = videoUrl; // fallback (rough sync; browsers without captureStream)
    };
    if (v.readyState >= 1) attach();
    else v.addEventListener('loadedmetadata', attach, { once: true });
    return () => {
      try {
        bg.srcObject = null;
      } catch {
        /* noop */
      }
    };
  }, [videoUrl]);

  return (
    <div className="studio-center">
      <div
        className="studio-stage"
        style={{
          aspectRatio: `${dims.w} / ${dims.h}`,
          // The stage width is min(container width, container height × ratio) so
          // it always fits BOTH axes of the center box (see .studio-center's
          // container-type). Height follows from aspect-ratio.
          ['--ar' as string]: dims.w / dims.h,
        }}
      >
        {videoUrl && <video ref={bgRef} className="studio-video-bg" muted playsInline aria-hidden />}
        {/* Content layer - this is what transitions transform (video + B-roll). */}
        <div className="studio-content">
          {videoUrl ? (
            <video
              ref={videoRef}
              src={videoUrl}
              className="studio-video"
              playsInline
              crossOrigin="anonymous"
            />
          ) : (
            <div className="studio-video studio-video-empty">Load a video</div>
          )}
        </div>
        {/* B-roll is a stage-level layer (not inside .studio-content) so it can
            sit ABOVE the person cut-out in the depth composite - a cover clip
            replaces the scene, an overlay floats over it. Still below captions. */}
        <BrollLayer className={showDepth ? 'depth-mode' : undefined} />
        {showDepth ? (
          <>
            {/* Behind hooks → person cut-out (alpha WebM) → captions + front hooks. */}
            <CaptionCanvas
              words={words}
              segments={segments}
              style={style}
              placement={placement}
              styleFor={(seg) => ({ style: styleForSegment(seg), placement: placementForSegment(seg) })}
              hooks={hooks}
              footageRef={videoRef}
              track={cutout.track}
              layer="behind"
              captionsBehind={captionsBehind}
              width={dims.w}
              height={dims.h}
            />
            <video
              ref={personRef}
              className="depth-layer"
              src={cutout.url!}
              muted
              playsInline
              preload="auto"
              aria-hidden
            />
            <CaptionCanvas
              words={words}
              segments={segments}
              style={style}
              placement={placement}
              styleFor={(seg) => ({ style: styleForSegment(seg), placement: placementForSegment(seg) })}
              hooks={hooks}
              footageRef={videoRef}
              track={cutout.track}
              layer="front"
              captionsBehind={captionsBehind}
              width={dims.w}
              height={dims.h}
            />
          </>
        ) : (
          <CaptionCanvas
            words={words}
            segments={segments}
            style={style}
            placement={placement}
            styleFor={(seg) => ({ style: styleForSegment(seg), placement: placementForSegment(seg) })}
            hooks={hooks}
            footageRef={videoRef}
            track={cutout.track}
            width={dims.w}
            height={dims.h}
          />
        )}
        <TransitionOverlay />
        <SafeZoneOverlay />
      </div>
      <AudioEngine />
      <SfxPlayer />
    </div>
  );
}
